import { UUID } from '@finxone-platform/shared/base-types';
import {
  AccountProviderService,
  CreatePageTemplateDto,
  CreatePageTemplateTagDto,
  PageTemplateConfig,
  PageTemplateFormatDto,
  PaymentProvider,
  PersonaZones,
  RelevantBaasTags,
  Role,
  WorkflowPageConfig,
  formatNameAsUrl,
} from '@finxone-platform/shared/sys-config-types';

export function extractPageTemplatesAndZonesFromPersona(payload: {
  persona: Role;
  tags?: CreatePageTemplateTagDto[];
}): { pageTemplates: CreatePageTemplateDto[]; personaZones: PersonaZones[] } {
  const entries = Object.entries(payload.persona.zones);
  const pageTemplates: CreatePageTemplateDto[] = [];
  const personaZones = [];

  for (const [zoneKey, zone] of entries) {
    if (zone === null) continue;
    personaZones.push({
      label: formatNameAsUrl(zoneKey).replace('-', ' '),
      value: zoneKey,
    });

    const mobilePages = (zone?.canvases.subscription['default']?.mobile?.pages as WorkflowPageConfig[]) ?? [];
    const tabletPages = (zone?.canvases.subscription['default']?.tablet?.pages as WorkflowPageConfig[]) ?? [];
    const desktopPages =
      (zone?.canvases.subscription['default']?.desktop?.pages as WorkflowPageConfig[]) ?? [];

    // Mobile pages
    for (const page of mobilePages) {
      const template: PageTemplateConfig = {} as any;
      template.mobile = page;
      template.tablet = tabletPages.find((tabletPage) => tabletPage.name === page.name) ?? page;
      template.desktop = desktopPages.find((desktopPage) => desktopPage.name === page.name) ?? page;
      const pageTemplate = createPageTemplateConfig(page, zoneKey, payload, template);
      pageTemplates.push(pageTemplate);
    }

    // Tablet pages
    for (const page of tabletPages) {
      // Avoid duplicate templates for pages already processed in mobile
      if (mobilePages.some((m) => m.name === page.name)) continue;
      const template: PageTemplateConfig = {} as any;
      template.mobile = mobilePages.find((mobilePage) => mobilePage.name === page.name) ?? page;
      template.tablet = page;
      template.desktop = desktopPages.find((desktopPage) => desktopPage.name === page.name) ?? page;
      const pageTemplate = createPageTemplateConfig(page, zoneKey, payload, template);
      pageTemplates.push(pageTemplate);
    }

    // Desktop pages
    for (const page of desktopPages) {
      // Avoid duplicate templates for pages already processed in mobile or tablet
      if (mobilePages.some((m) => m.name === page.name) || tabletPages.some((t) => t.name === page.name))
        continue;
      const template: PageTemplateConfig = {} as any;
      template.mobile = mobilePages.find((mobilePage) => mobilePage.name === page.name) ?? page;
      template.tablet = tabletPages.find((tabletPage) => tabletPage.name === page.name) ?? page;
      template.desktop = page;
      const pageTemplate = createPageTemplateConfig(page, zoneKey, payload, template);
      pageTemplates.push(pageTemplate);
    }
  }
  return { pageTemplates, personaZones };
}

function createPageTemplateConfig(
  page: WorkflowPageConfig,
  zoneKey: string,
  payload: {
    persona: Role;
    tags?: CreatePageTemplateTagDto[];
  },
  template: PageTemplateConfig,
): CreatePageTemplateDto {
  const pageTemplate: CreatePageTemplateDto = new CreatePageTemplateDto();
  pageTemplate.devicesPageConfig = template;
  pageTemplate.pageKey = page.pageConfiguration.url;
  pageTemplate.zoneKey = zoneKey;
  pageTemplate.name = page.title;
  pageTemplate.tags = payload.tags ?? [];
  pageTemplate.tempTrackingId = new UUID().toString();
  return pageTemplate;
}

export function parseRelevantBaasTags(baasConfiguration: AccountProviderService): RelevantBaasTags[] {
  const baasTags: string[] = [baasConfiguration.provider];
  if (baasConfiguration.provider === PaymentProvider.INTEGRATED_FINANCE) {
    if (baasConfiguration?.integratedFinanceConfig?.serviceProvider) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baasTags.push('if-' + baasConfiguration.integratedFinanceConfig.serviceProvider.toLowerCase());
    }
  }

  return baasTags as RelevantBaasTags[];
}

export function findFormatByName(
  formatName: string,
  formatList: PageTemplateFormatDto[],
): PageTemplateFormatDto | undefined {
  return formatList.find((f: PageTemplateFormatDto) => f.name === formatName);
}

export function findFormatNameById(
  formatId: string | null | undefined,
  formatList: PageTemplateFormatDto[],
): string {
  return formatList.find((f: PageTemplateFormatDto) => f.id === formatId)?.name ?? '';
}
